import { useEffect, useState } from 'react'
import { SlackUser } from './useSlackUsers'

interface MentionState {
  isActive: boolean
  searchText: string
  startPosition: number
}

export const useMentions = (inputValue: string, cursorPosition: number, allUsers: SlackUser[]) => {
  const [mentionState, setMentionState] = useState<MentionState>({
    isActive: false,
    searchText: '',
    startPosition: 0,
  })
  const [filteredUsers, setFilteredUsers] = useState<SlackUser[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [taggedUsers, setTaggedUsers] = useState<SlackUser[]>([])

  // Detect @ mentions and extract search text
  useEffect(() => {
    const textBeforeCursor = inputValue.slice(0, cursorPosition)

    // Only consider the current line
    const lastLineBreak = textBeforeCursor.lastIndexOf('\n')
    const currentLine = textBeforeCursor.slice(lastLineBreak + 1)

    const lastAtInLine = currentLine.lastIndexOf('@')
    if (lastAtInLine === -1) {
      setMentionState({ isActive: false, searchText: '', startPosition: 0 })
      return
    }

    const textAfterAt = currentLine.slice(lastAtInLine + 1)

    // Any whitespace (space, newline, tab) ends the mention
    if (/\s/.test(textAfterAt)) {
      setMentionState({ isActive: false, searchText: '', startPosition: 0 })
      return
    }

    // Global index of '@' in the whole input
    const mentionStart = lastLineBreak + 1 + lastAtInLine

    setMentionState({
      isActive: true,
      searchText: textAfterAt,
      startPosition: mentionStart,
    })
  }, [inputValue, cursorPosition])

  // Filter users based on search text
  useEffect(() => {
    if (!mentionState.isActive) {
      setFilteredUsers([])
      setSelectedIndex(0)
      return
    }

    const searchLower = mentionState.searchText.toLowerCase()
    const filtered = allUsers.filter(user => {
      const displayName = user.displayName.toLowerCase()

      return displayName.includes(searchLower)
    })

    setFilteredUsers(filtered.slice(0, 10)) // Limit to 10 suggestions
    // setSelectedIndex(0)
  }, [mentionState, allUsers])

  // Validate tagged users still exist in the input text
  useEffect(() => {
    if (taggedUsers.length === 0) return

    const stillTagged = taggedUsers.filter(user => {
      // Check if @DisplayName exists in the text
      const mentionPattern = `@${user.displayName}`
      return inputValue.includes(mentionPattern)
    })

    console.log('tagged: ', stillTagged)

    // Only update if the list changed
    if (stillTagged.length !== taggedUsers.length) {
      setTaggedUsers(stillTagged)
    }
  }, [inputValue, taggedUsers])

  const selectUser = (user: SlackUser) => {
    const displayName = user.displayName

    const mentionStart = mentionState.startPosition // index of '@'
    const mentionEnd = mentionStart + 1 + mentionState.searchText.length
    //                         ^ '@'         ^ length of whatever they've typed after '@'

    const beforeMention = inputValue.slice(0, mentionStart)
    const afterMention = inputValue.slice(mentionEnd)

    // Insert @DisplayName + space
    const newValue = `${beforeMention}@${displayName} ${afterMention}`

    // Cursor goes right after "@DisplayName "
    const newCursorPosition = beforeMention.length + 1 + displayName.length + 1

    setTaggedUsers(prev => {
      const alreadyTagged = prev.some(u => u.id === user.id)
      return alreadyTagged ? prev : [...prev, user]
    })

    setMentionState({
      isActive: false,
      searchText: '',
      startPosition: newCursorPosition,
    })

    return { newValue, newCursorPosition }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!mentionState.isActive || filteredUsers.length === 0) {
      return false
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setSelectedIndex(prev => (prev + 1) % filteredUsers.length)
        return true
      case 'ArrowUp':
        event.preventDefault()
        setSelectedIndex(prev => (prev - 1 + filteredUsers.length) % filteredUsers.length)
        return true
      case 'Enter':
      case 'Tab':
        if (filteredUsers[selectedIndex]) {
          event.preventDefault()
          return selectUser(filteredUsers[selectedIndex])
        }
        return false
      case 'Escape':
        event.preventDefault()
        setMentionState({ isActive: false, searchText: '', startPosition: 0 })
        return true
      default:
        return false
    }
  }

  const clearTaggedUsers = () => {
    setTaggedUsers([])
  }

  return {
    isShowingSuggestions: mentionState.isActive && filteredUsers.length > 0,
    filteredUsers,
    selectedIndex,
    selectUser,
    handleKeyDown,
    taggedUsers,
    clearTaggedUsers,
  }
}
